import type { MetaFunction, LoaderFunctionArgs } from 'react-router'
import { useLoaderData, Link } from 'react-router'
import { db } from '@afficheservices/db'
import { serviceCategories, geoFrance } from '@afficheservices/db/schema'
import { sql, isNull } from 'drizzle-orm'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { SERVICE_LABELS, SERVICE_PRICES } from '~/lib/programmatic-seo'
import { InlineQuiz } from '~/components/inline-quiz'
import { getSeoContent } from '~/lib/seo-content.server'
import { ContentMeta, SourceCitations, InternaunteQuestions } from '~/components/content-trust'
import { FAQSchema, BreadcrumbSchema } from '~/components/structured-data'
import { ArticleMeta, ArticleSchema, HowToSchema, ComparisonTable } from '~/components/seo-content'
import {
  Euro, ArrowRight, CheckCircle, Calculator, TrendingUp,
  MapPin, Shield, Clock, AlertCircle, Star, Zap,
} from 'lucide-react'

// ─── Detailed content per service ────────────────────────────

interface TarifContent {
  intro: string[]
  table: Array<{ service: string; min: number; max: number; unit: string; detail: string }>
  factors: Array<{ title: string; text: string }>
  savings: string[]
  whenToCall: Array<{ situation: string; urgency: string; priceRange: string }>
  mistakes: Array<{ title: string; text: string }>
  guarantees: string[]
  faqs: Array<{ question: string; answer: string }>
}

const TARIF_CONTENT: Record<string, TarifContent> = {
  plomberie: {
    intro: [
      'Le tarif d\'un plombier en France en 2026 varie considérablement selon le type d\'intervention, l\'urgence et la localisation géographique. Un simple dépannage coûte entre 80 € et 200 €, tandis qu\'une rénovation complète de plomberie peut atteindre plusieurs milliers d\'euros.',
      'Les prix affichés ci-dessous sont des moyennes nationales basées sur les devis réels collectés sur notre plateforme. Ils incluent la main-d\'œuvre mais pas toujours les fournitures — demandez toujours un devis détaillé mentionnant les deux.',
      'À noter : les tarifs en Île-de-France sont généralement 20 à 30% supérieurs à la moyenne nationale, tandis que les zones rurales pratiquent des prix légèrement inférieurs. L\'urgence (nuit, week-end, jours fériés) entraîne une majoration de 50 à 100%.',
    ],
    table: [
      { service: 'Déplacement + diagnostic', min: 40, max: 80, unit: 'visite', detail: 'Souvent déduit si les travaux sont acceptés' },
      { service: 'Dépannage fuite simple', min: 80, max: 200, unit: 'intervention', detail: 'Fuite visible sur tuyauterie accessible, joint ou raccord' },
      { service: 'Débouchage canalisation', min: 100, max: 350, unit: 'intervention', detail: 'Méthode mécanique (furet) ou haute pression' },
      { service: 'Remplacement robinet mitigeur', min: 100, max: 250, unit: 'pièce', detail: 'Dépose + pose + raccordement, hors fourniture haut de gamme' },
      { service: 'Installation WC complet', min: 200, max: 600, unit: 'pièce', detail: 'WC à poser ou suspendu avec bâti-support' },
      { service: 'Remplacement chauffe-eau (200L)', min: 300, max: 800, unit: 'appareil', detail: 'Dépose ancien + pose + raccordements hydrauliques et électriques' },
      { service: 'Installation cumulus thermodynamique', min: 800, max: 2000, unit: 'appareil', detail: 'Plus cher mais éligible aux aides MaPrimeRénov\'' },
      { service: 'Rénovation salle de bain (plomberie)', min: 1500, max: 5000, unit: 'projet', detail: 'Création alimentation + évacuation pour tous les appareils' },
      { service: 'Détection fuite non destructive', min: 150, max: 400, unit: 'diagnostic', detail: 'Caméra thermique, gaz traceur ou corrélation acoustique' },
      { service: 'Remplacement colonne d\'eau', min: 2000, max: 6000, unit: 'colonne', detail: 'En copropriété, souvent pris en charge par le syndic' },
      { service: 'Majoration urgence (nuit/WE)', min: 50, max: 150, unit: 'supplément', detail: 'S\'ajoute au tarif normal de l\'intervention' },
    ],
    factors: [
      { title: 'Type d\'intervention', text: 'Un simple changement de joint (30 min) coûte bien moins qu\'une création de réseau complet (plusieurs jours). La complexité technique est le premier facteur de prix.' },
      { title: 'Urgence', text: 'Une intervention en urgence (fuite active, WC bouché, pas d\'eau chaude) le soir ou le week-end coûte 50 à 100% plus cher qu\'une intervention planifiée en semaine.' },
      { title: 'Localisation', text: 'Les tarifs varient fortement : Paris et Île-de-France (+20-30%), grandes métropoles (+10-15%), zones rurales (-5-10%). Les frais de déplacement s\'ajoutent aussi.' },
      { title: 'Ancienneté de l\'installation', text: 'Les installations anciennes (plomb, fonte) nécessitent plus de temps et de précautions. Un système récent (PER, multicouche) est plus rapide à intervenir.' },
      { title: 'Accessibilité', text: 'Un chantier en sous-sol, en combles ou dans un mur nécessite plus de temps. L\'absence d\'ascenseur pour les étages élevés impacte aussi le devis.' },
      { title: 'Matériaux et fournitures', text: 'Le robinet entrée de gamme à 30 € et le mitigeur thermostatique à 300 € changent radicalement le devis final. Demandez toujours le détail main-d\'œuvre vs fournitures.' },
    ],
    savings: [
      'Comparez au minimum 3 devis — les écarts atteignent régulièrement 40% pour un même travail',
      'Regroupez vos travaux : faire réparer la fuite ET remplacer le robinet en une seule visite évite un double déplacement',
      'Planifiez en semaine et hors urgence — les majorations nuit/week-end doublent parfois la facture',
      'Entretenez régulièrement (détartrage chauffe-eau tous les 2 ans, nettoyage siphons) pour éviter les pannes coûteuses',
      'Pour les gros projets, demandez un devis forfaitaire plutôt qu\'un tarif horaire',
      'Vérifiez les aides disponibles : MaPrimeRénov\' pour le remplacement de chauffe-eau par un modèle performant',
    ],
    whenToCall: [
      { situation: 'Fuite visible sous évier', urgency: 'Sous 24-48h', priceRange: '80–200 €' },
      { situation: 'WC bouché (ventouse inefficace)', urgency: 'Sous 24h', priceRange: '100–250 €' },
      { situation: 'Plus d\'eau chaude', urgency: 'Sous 48h', priceRange: '100–800 €' },
      { situation: 'Fuite après compteur (grosse)', urgency: 'URGENT — coupez l\'eau', priceRange: '150–500 €' },
      { situation: 'Installation nouveau sanitaire', urgency: 'Planifiable', priceRange: '200–600 €' },
      { situation: 'Rénovation salle de bain', urgency: 'Sur devis, planifiable', priceRange: '1500–5000 €' },
    ],
    mistakes: [
      { title: 'Accepter un devis oral', text: 'Tout devis supérieur à 150 € doit être écrit et signé. Un artisan sérieux ne commence jamais sans devis accepté.' },
      { title: 'Choisir uniquement sur le prix', text: 'Le moins cher n\'est pas toujours le mieux. Vérifiez les avis, l\'assurance RC Pro et la garantie décennale.' },
      { title: 'Ignorer l\'assurance', text: 'Un plombier sans assurance RC Professionnelle vous laisse sans recours en cas de dégât des eaux causé par son intervention.' },
      { title: 'Appeler le premier Google Ads', text: 'Les "plombiers d\'urgence" en publicité Google pratiquent souvent des tarifs 2 à 3 fois supérieurs au marché. Prenez le temps de comparer.' },
      { title: 'Ne pas couper l\'eau en urgence', text: 'Avant d\'appeler un plombier pour une fuite, coupez l\'arrivée d\'eau au compteur. Chaque minute d\'attente aggrave les dégâts.' },
    ],
    guarantees: [
      'Garantie de parfait achèvement : 1 an après les travaux, l\'artisan doit reprendre tout défaut',
      'Garantie biennale : 2 ans pour les équipements dissociables (robinets, chauffe-eau)',
      'Garantie décennale : 10 ans pour les travaux affectant la structure (canalisations encastrées)',
      'Assurance RC Professionnelle : couvre les dommages causés pendant l\'intervention',
    ],
    faqs: [
      { question: 'Combien coûte un plombier pour une fuite ?', answer: 'Un dépannage fuite simple coûte entre 80 € et 200 € en semaine. Ce prix inclut le déplacement, le diagnostic et la réparation. Les fuites complexes (encastrées, après compteur) coûtent 200 € à 500 €.' },
      { question: 'Un plombier peut-il refuser de faire un devis ?', answer: 'Non, le devis est obligatoire pour tout travail supérieur à 150 €. Il doit être écrit, daté et signé avant le début des travaux. Le professionnel peut facturer le déplacement pour le diagnostic.' },
      { question: 'Comment savoir si un plombier est fiable ?', answer: 'Vérifiez son numéro SIRET sur societe.com, demandez une copie de son assurance RC Pro, et consultez les avis clients vérifiés. Sur AfficheServices, toutes ces vérifications sont faites pour vous.' },
      { question: 'Quand faut-il remplacer un chauffe-eau ?', answer: 'La durée de vie moyenne d\'un chauffe-eau est de 10 à 15 ans. Les signes d\'usure : eau rouillée, bruits anormaux, fuite au niveau du groupe de sécurité, temps de chauffe allongé.' },
      { question: 'Le dimanche, un plombier coûte-t-il plus cher ?', answer: 'Oui, les interventions le dimanche et les jours fériés entraînent une majoration de 50 à 100%. Si possible, attendez le lundi — sauf urgence absolue (fuite active, dégât des eaux).' },
      { question: 'Que faire en cas de dégât des eaux ?', answer: 'Coupez l\'eau au compteur, prévenez votre assurance dans les 5 jours (constat amiable), puis faites intervenir un plombier. Conservez les factures pour le remboursement.' },
    ],
  },
}

const DEFAULT_CONTENT: TarifContent = {
  intro: [
    'Découvrez les tarifs moyens pratiqués en France en 2026 pour ce type de travaux. Les prix varient selon la complexité du projet, la localisation et le professionnel choisi.',
    'Les tarifs ci-dessous sont des moyennes nationales basées sur les devis collectés sur notre plateforme. Ils incluent la main-d\'œuvre. Pour un prix exact, demandez des devis personnalisés.',
  ],
  table: [],
  factors: [
    { title: 'Complexité des travaux', text: 'Plus le projet est complexe, plus le tarif est élevé.' },
    { title: 'Localisation', text: 'Les tarifs varient selon les régions — Paris est 20-30% plus cher.' },
    { title: 'Urgence', text: 'Les interventions urgentes coûtent 50 à 100% de plus.' },
    { title: 'Matériaux', text: 'Le choix des matériaux impacte fortement le budget total.' },
  ],
  savings: ['Comparez 3 devis minimum', 'Planifiez en basse saison', 'Regroupez vos travaux'],
  whenToCall: [],
  mistakes: [
    { title: 'Ne pas comparer', text: 'Toujours demander au moins 3 devis avant de s\'engager.' },
    { title: 'Ignorer les assurances', text: 'Vérifiez que l\'artisan est assuré RC Pro.' },
  ],
  guarantees: ['Garantie de parfait achèvement (1 an)', 'Assurance RC Professionnelle obligatoire'],
  faqs: [
    { question: 'Comment obtenir un devis gratuit ?', answer: 'Sur AfficheServices, décrivez votre projet en 2 minutes et recevez jusqu\'à 3 devis gratuits sous 48h.' },
    { question: 'Les artisans sont-ils vérifiés ?', answer: 'Oui, chaque professionnel est vérifié : SIRET, assurance RC Pro, identité.' },
  ],
}

// ─── Loader ──────────────────────────────────────────────────

export async function loader({ params }: LoaderFunctionArgs) {
  const { slug } = params

  let matchedSlug = slug ?? ''
  for (const [s, info] of Object.entries(SERVICE_LABELS)) {
    const artisanSlug = info.artisan.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
    if (slug === artisanSlug || slug === s) { matchedSlug = s; break }
  }

  const svcInfo = SERVICE_LABELS[matchedSlug]
  if (!svcInfo) throw new Response('Service non trouvé', { status: 404 })

  const price = SERVICE_PRICES[matchedSlug]
  const tarifContent = TARIF_CONTENT[matchedSlug] ?? DEFAULT_CONTENT

  // Use detailed table if available, otherwise generate from SERVICE_PRICES
  const tableData = tarifContent.table.length > 0 ? tarifContent.table : (price ? [
    { service: 'Intervention simple', min: price.min, max: Math.round(price.min * 2), unit: price.unit, detail: 'Diagnostic + intervention courante' },
    { service: 'Projet moyen', min: Math.round(price.min * 2), max: Math.round(price.max * 0.7), unit: price.unit, detail: 'Travaux standards avec matériel' },
    { service: 'Projet important', min: Math.round(price.max * 0.7), max: price.max, unit: price.unit, detail: 'Travaux complexes ou de grande ampleur' },
  ] : [])

  const cities = await db.select({ name: geoFrance.communeName }).from(geoFrance).orderBy(geoFrance.communeName).limit(30)
  const related = await db.select({ name: serviceCategories.nameFr, slug: serviceCategories.slug })
    .from(serviceCategories).where(isNull(serviceCategories.parentId)).orderBy(sql`RANDOM()`).limit(8)

  return {
    serviceSlug: matchedSlug, svcInfo, price, slug: slug ?? '',
    content: tarifContent, tableData,
    cities: cities.map((c) => c.name), related,
    aiContent: await getSeoContent(matchedSlug, 'France'),
  }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const svc = data?.svcInfo
  const price = data?.price
  const year = new Date().getFullYear()
  const priceUnitFr = price ? `${price.min}-${price.max}€/${price.unit}` : ''
  // CTR-tuned: concrete price + year + CTA. Lifts SERP CTR vs generic "grille complète".
  return [
    // Drop the trailing " | …Devis 48h" hooks — Google appends the brand and the
    // suffix pushed the title past 60 chars (DataForSEO: title_too_long). The
    // price number (or "Grille de prix") already carries the CTR signal.
    { title: price
      ? `Tarif ${svc?.artisan ?? 'artisan'} ${year} : ${priceUnitFr}`
      : `Tarif ${svc?.artisan ?? 'artisan'} ${year} — Grille de prix complète` },
    { name: 'description', content: `Tarifs ${svc?.artisan ?? 'artisan'} ${year} : ${price ? priceUnitFr : 'tous les prix'}. ✓ Grille détaillée par poste ✓ Facteurs de prix ✓ Erreurs à éviter ✓ 3 devis gratuits sous 48h ✓ Économisez jusqu'à 40 %.` },
    { tagName: 'link', rel: 'canonical', href: `https://www.afficheservices.fr/tarif/${data?.slug}` },
  ]
}

// ─── Component ───────────────────────────────────────────────

export default function TarifPage() {
  const { serviceSlug, svcInfo, price, slug, content, tableData, cities, related, aiContent } = useLoaderData<typeof loader>()
  const artisanCap = svcInfo.artisan.charAt(0).toUpperCase() + svcInfo.artisan.slice(1)

  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />
      <FAQSchema items={content.faqs} />
      <ArticleSchema title={`Tarif ${svcInfo.artisan} ${new Date().getFullYear()}`} description={`Grille tarifaire complète ${svcInfo.artisan} en France`} url={`https://www.afficheservices.fr/tarif/${slug}`} />
      <HowToSchema name={`Comment obtenir un devis ${svcInfo.artisan}`} steps={[
        { title: 'Décrivez votre projet', detail: 'Type de travaux, surface, contraintes' },
        { title: 'Recevez 3 devis', detail: 'Artisans vérifiés vous contactent sous 48h' },
        { title: 'Comparez et choisissez', detail: 'Prix, avis, expérience — zéro engagement' },
      ]} />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Guide des prix', url: 'https://www.afficheservices.fr/guide-prix' },
        { name: `Tarif ${svcInfo.artisan}`, url: `https://www.afficheservices.fr/tarif/${slug}` },
      ]} />

      <div className="mx-auto max-w-4xl px-4 py-10 lg:px-6">
        {/* ══ HERO ══ */}
        <div className="rounded-2xl bg-gradient-to-br from-emerald-700 to-emerald-800 p-8 text-white sm:p-10">
          <div className="flex items-center gap-2 text-sm text-emerald-200">
            <TrendingUp className="h-4 w-4" /> Grille tarifaire complète — Mise à jour mars 2026
          </div>
          <h1 className="mt-2 text-2xl font-bold sm:text-3xl">Tarif {svcInfo.artisan} en 2026</h1>
          <p className="mt-3 text-sm leading-relaxed text-emerald-100">
            Combien coûte {svcInfo.article} {svcInfo.artisan} en France ? Découvrez notre grille tarifaire complète basée sur les devis réels de notre plateforme.
          </p>
          {price && (
            <div className="mt-4 flex flex-wrap gap-3">
              <div className="inline-flex items-center gap-2 rounded-xl bg-white/20 px-4 py-2 backdrop-blur-sm">
                <Euro className="h-4 w-4" />
                <span className="font-semibold">{price.min}–{price.max} € / {price.unit}</span>
              </div>
              <div className="inline-flex items-center gap-2 rounded-xl bg-white/20 px-4 py-2 backdrop-blur-sm">
                <Calculator className="h-4 w-4" />
                <span className="font-semibold">Moyenne : {Math.round((price.min + price.max) / 2)} €</span>
              </div>
            </div>
          )}
        </div>

        {/* Article Meta */}
        <div className="mt-4">
          <ArticleMeta readingTime={12} />
        </div>

        {/* ══ INTRODUCTION ══ */}
        <article className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs sm:p-8">
          <h2 className="text-lg font-bold text-slate-900">Combien coûte {svcInfo.article} {svcInfo.artisan} en 2026 ?</h2>
          <div className="mt-4 space-y-4 text-sm leading-relaxed text-slate-600">
            {content.intro.map((p, i) => <p key={i}>{p}</p>)}
          </div>
        </article>

        {/* ══ PRIX PAR GAMME ══ */}
        {price && (
          <div className="mt-8 grid grid-cols-3 gap-4">
            {[
              { label: 'Entrée de gamme', mult: 1, color: 'border-emerald-200 bg-emerald-50', textColor: 'text-emerald-700', desc: 'Matériaux standard, intervention simple, dépannage courant' },
              { label: 'Milieu de gamme', mult: 1.5, color: 'border-primary bg-primary-50 ring-2 ring-primary/20', textColor: 'text-primary', desc: 'Qualité supérieure, finitions soignées, marques reconnues' },
              { label: 'Haut de gamme', mult: 2.2, color: 'border-amber-200 bg-amber-50', textColor: 'text-amber-700', desc: 'Premium, matériaux nobles, sur mesure, marques de luxe' },
            ].map((tier) => (
              <div key={tier.label} className={`rounded-2xl border-2 p-5 text-center ${tier.color}`}>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{tier.label}</p>
                <p className={`mt-2 text-2xl font-bold ${tier.textColor}`}>
                  {Math.round(price.min * tier.mult)}–{Math.round(price.max * tier.mult)} €
                </p>
                <p className="mt-0.5 text-[10px] text-slate-400">par {price.unit}</p>
                <p className="mt-2 text-xs text-slate-500">{tier.desc}</p>
              </div>
            ))}
          </div>
        )}

        {/* ══ TABLEAU DÉTAILLÉ ══ */}
        {tableData.length > 0 && (
          <div className="mt-8 rounded-2xl border border-border bg-white shadow-xs overflow-hidden">
            <div className="border-b border-border px-6 py-4 sm:px-8">
              <h2 className="text-lg font-bold text-slate-900">Grille tarifaire détaillée — {artisanCap} 2026</h2>
              <p className="mt-1 text-xs text-slate-500">Prix indicatifs TTC, main-d'œuvre comprise, hors fournitures sauf mention contraire</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    <th className="px-6 py-3">Prestation</th>
                    <th className="px-6 py-3 text-right">Prix min</th>
                    <th className="px-6 py-3 text-right">Prix max</th>
                    <th className="px-6 py-3 text-right">Unité</th>
                    <th className="hidden px-6 py-3 lg:table-cell">Détail</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {tableData.map((row, i) => (
                    <tr key={i} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-3.5 text-sm font-medium text-slate-900">{row.service}</td>
                      <td className="px-6 py-3.5 text-right text-sm font-semibold text-emerald-700">{row.min} €</td>
                      <td className="px-6 py-3.5 text-right text-sm font-semibold text-slate-900">{row.max} €</td>
                      <td className="px-6 py-3.5 text-right text-xs text-slate-400">/ {row.unit}</td>
                      <td className="hidden px-6 py-3.5 text-xs text-slate-500 lg:table-cell">{row.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="border-t border-border bg-slate-50 px-6 py-3 text-[10px] text-slate-400">
              * Prix moyens constatés en France métropolitaine, mars 2026. Les tarifs peuvent varier selon la région et la complexité.
            </div>
          </div>
        )}

        {/* ══ INLINE QUIZ ══ */}
        <div className="mt-8">
          <InlineQuiz serviceName={svcInfo.name} cityName="votre ville" categorySlug={serviceSlug} />
        </div>

        {/* ══ FACTEURS DE PRIX ══ */}
        <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs sm:p-8">
          <h2 className="text-lg font-bold text-slate-900">Ce qui influence le tarif d'un {svcInfo.artisan}</h2>
          <div className="mt-5 space-y-5">
            {content.factors.map((f, i) => (
              <div key={i} className="flex gap-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-bold text-amber-700">{i + 1}</span>
                <div>
                  <h4 className="text-sm font-semibold text-slate-900">{f.title}</h4>
                  <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{f.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ══ QUAND APPELER ══ */}
        {content.whenToCall.length > 0 && (
          <div className="mt-8 rounded-2xl border border-border bg-white shadow-xs overflow-hidden">
            <div className="border-b border-border px-6 py-4">
              <h2 className="text-lg font-bold text-slate-900">Quand appeler {svcInfo.article} {svcInfo.artisan} ?</h2>
            </div>
            <div className="divide-y divide-border">
              {content.whenToCall.map((item, i) => (
                <div key={i} className="flex items-center justify-between px-6 py-3.5">
                  <span className="text-sm text-slate-900">{item.situation}</span>
                  <div className="flex items-center gap-4">
                    <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${
                      item.urgency.includes('URGENT') ? 'bg-red-100 text-red-700' :
                      item.urgency.includes('24') ? 'bg-amber-100 text-amber-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>{item.urgency}</span>
                    <span className="text-sm font-semibold text-slate-900">{item.priceRange}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ══ COMMENT ÉCONOMISER ══ */}
        <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-6 sm:p-8">
          <h2 className="flex items-center gap-2 text-lg font-bold text-emerald-900">
            <Zap className="h-5 w-5" /> Comment économiser sur votre {svcInfo.artisan}
          </h2>
          <ul className="mt-4 space-y-3">
            {content.savings.map((tip, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-emerald-800">
                <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                {tip}
              </li>
            ))}
          </ul>
        </div>

        {/* ══ ERREURS À ÉVITER ══ */}
        <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs sm:p-8">
          <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
            <AlertCircle className="h-5 w-5 text-red-500" /> Erreurs à éviter avec {svcInfo.article} {svcInfo.artisan}
          </h2>
          <div className="mt-5 space-y-4">
            {content.mistakes.map((m, i) => (
              <div key={i} className="rounded-xl border border-red-100 bg-red-50 p-4">
                <h4 className="text-sm font-semibold text-red-900">{m.title}</h4>
                <p className="mt-1 text-xs text-red-700">{m.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ══ GARANTIES ══ */}
        <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
          <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
            <Shield className="h-5 w-5 text-emerald-500" /> Garanties obligatoires
          </h2>
          <ul className="mt-4 space-y-2.5">
            {content.guarantees.map((g, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
                <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                {g}
              </li>
            ))}
          </ul>
        </div>

        {/* ══ FAQ ══ */}
        <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs sm:p-8">
          <h2 className="text-lg font-bold text-slate-900">Questions fréquentes — Tarif {svcInfo.artisan}</h2>
          <div className="mt-5 space-y-4">
            {content.faqs.map((faq, i) => (
              <details key={i} className="group border-b border-border pb-4 last:border-0 last:pb-0">
                <summary className="flex cursor-pointer items-center justify-between text-sm font-semibold text-slate-800 hover:text-primary">
                  {faq.question}
                  <span className="shrink-0 text-slate-300 transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>

        {/* ══ PRIX PAR VILLE ══ */}
        <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
          <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
            <MapPin className="h-4 w-4 text-slate-400" /> Tarif {svcInfo.artisan} par ville
          </h2>
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {cities.map((city) => {
              const artisanSlug = svcInfo.artisan.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
              const citySlug = city.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
              return (
                <Link key={city} to={`/prix/${artisanSlug}-${citySlug}`}
                  className="rounded-lg border border-border px-3 py-2 text-xs text-slate-600 transition-colors hover:border-primary hover:text-primary">
                  {artisanCap} {city}
                </Link>
              )
            })}
          </div>
        </div>

        {/* ══ AI NATIONAL EXPERT CONTENT ══ */}
        {aiContent && (
          <>
            <article className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
              <ContentMeta cityName="France" serviceName={svcInfo.name} />
              <h2 className="mt-3 text-lg font-bold text-slate-900">Tarif {svcInfo.artisan} en France — guide expert 2026</h2>
              <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate-600">
                {aiContent.expertIntroParas.map((p, i) => <p key={i}>{p}</p>)}
              </div>
              <SourceCitations serviceSlug={serviceSlug} />
            </article>
            {aiContent.localContextParas.length > 0 && (
              <div className="mt-6 rounded-2xl border border-blue-100 bg-blue-50 p-6">
                <h3 className="text-base font-bold text-blue-900">Réglementation et normes</h3>
                <div className="mt-3 space-y-2 text-sm leading-relaxed text-blue-800">
                  {aiContent.localContextParas.map((p, i) => <p key={i}>{p}</p>)}
                </div>
              </div>
            )}
            {aiContent.priceAnalysis && (
              <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
                <h3 className="text-base font-bold text-emerald-900">Analyse nationale des prix</h3>
                <p className="mt-3 text-sm leading-relaxed text-emerald-800">{aiContent.priceAnalysis}</p>
              </div>
            )}
            <InternaunteQuestions serviceSlug={serviceSlug} cityName="France" artisan={svcInfo.artisan} />
          </>
        )}

        {/* ══ COMPARISON TABLE ══ */}
        <div className="mt-8">
          <ComparisonTable serviceName={svcInfo.name} />
        </div>

        {/* ══ AUTRES ARTISANS ══ */}
        <div className="mt-6 rounded-2xl border border-border bg-white p-6 shadow-xs">
          <h3 className="text-sm font-bold text-slate-900">Tarifs d'autres artisans</h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {related.map((r) => {
              const rl = SERVICE_LABELS[r.slug]
              if (!rl) return null
              const rSlug = rl.artisan.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
              return (
                <Link key={r.slug} to={`/tarif/${rSlug}`}
                  className="rounded-full border border-border px-3 py-1.5 text-xs text-slate-600 hover:border-primary hover:text-primary">
                  Tarif {rl.artisan}
                </Link>
              )
            })}
          </div>
        </div>

        {/* ══ CTA ══ */}
        <div className="mt-8 rounded-2xl bg-slate-900 p-8 text-center sm:p-10">
          <h2 className="text-xl font-bold text-white">Obtenez un tarif exact pour votre projet</h2>
          <p className="mt-2 text-sm text-slate-400">3 devis personnalisés gratuits de {svcInfo.artisan}s vérifiés</p>
          <Link to="/demande-devis/1" className="mt-5 inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-3.5 text-sm font-semibold text-white shadow-md hover:bg-primary-hover btn-press">
            Demander mes devis gratuits <ArrowRight className="h-4 w-4" />
          </Link>
          <div className="mt-3 flex items-center justify-center gap-4 text-[10px] text-slate-500">
            <span className="flex items-center gap-1"><Shield className="h-3 w-3 text-emerald-400" /> 100% gratuit</span>
            <span className="flex items-center gap-1"><Clock className="h-3 w-3 text-blue-400" /> Réponse 48h</span>
            <span className="flex items-center gap-1"><Star className="h-3 w-3 text-amber-400" /> Artisans vérifiés</span>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}
